/**
 * 語言切換工具函數
 * 負責處理語言選擇器的切換邏輯（路徑式語系）
 */

/**
 * 從 DOM 中讀取路由配置
 * 優先從 data-translations 讀取，fallback 到默認值
 */
function getRoutesConfig() {
  try {
    const headerElement = document.querySelector('[data-translations]');
    if (headerElement) {
      const translations = JSON.parse(headerElement.dataset.translations);
      if (translations?.routes_config) {
        return {
          supportedLocales: translations.routes_config.supported_locales || [],
          systemPaths: translations.routes_config.system_paths || [],
          staticPages: translations.routes_config.static_pages || []
        };
      }
    }
  } catch (error) {
    console.warn('Failed to load routes config from data attributes:', error);
  }

  // Fallback to hardcoded values if backend config not available
  // 目前只支援 zh-TW 和 en，其他語系保留檔案以備日後啟用
  return {
    supportedLocales: ['zh-TW', 'en'],
    systemPaths: ['teams', 'dashboard', 'student', 'admin', 'api', 'users', 'rails', 'devise', 'assets'],
    staticPages: ['about', 'faq', 'terms', 'privacy', 'referral-market']
  };
}

// 支援的語系列表（從 RoutesConfig 讀取）
let SUPPORTED_LOCALES = [];

// 系統路徑列表（從 RoutesConfig 讀取，不包含靜態頁面）
let SYSTEM_PATHS = [];

// 靜態頁面列表（從 RoutesConfig 讀取）
let STATIC_PAGES = [];

/**
 * 確保配置已載入（Lazy Initialization）
 * 無論何時調用，都能保證配置可用
 */
function ensureConfigLoaded() {
  // 如果配置還沒載入（數組為空），立即載入
  if (SUPPORTED_LOCALES.length === 0) {
    const config = getRoutesConfig();
    SUPPORTED_LOCALES = config.supportedLocales;
    SYSTEM_PATHS = config.systemPaths;
    STATIC_PAGES = config.staticPages;
  }
}

/**
 * 檢查路徑是否支援 locale URL（白名單模式）
 * @param {string} path - 要檢查的路徑
 * @returns {boolean} - 是否支援 locale URL
 */
function isLocaleUrlSupported(path) {
  // 確保配置已載入
  ensureConfigLoaded();

  // 移除開頭的 locale（如果有）
  const pathWithoutLocale = path.replace(/^\/[^\/]+/, (match) => {
    const segment = match.slice(1);
    // 不區分大小寫檢查是否為支援的 locale
    const isLocale = SUPPORTED_LOCALES.some(loc => loc.toLowerCase() === segment.toLowerCase());
    return isLocale ? '' : match;
  });

  const normalizedPath = pathWithoutLocale || '/';

  // 白名單：只有這些路徑支援 locale URL
  // 1. 首頁
  if (normalizedPath === '/') return true;


  // 3. 教師公開頁 /teachers/:slug
  if (normalizedPath.startsWith('/teachers/')) return true;

  // 4. Blog 頁面 /blog, /blog/:slug
  if (normalizedPath.startsWith('/blog')) return true;

  // 5. 推薦市場公開頁 /referral-market
  if (normalizedPath === '/referral-market') return true;

  // 6. 工具頁面 /tools, /tools/chinese-worksheet
  if (normalizedPath === '/tools' || normalizedPath.startsWith('/tools/')) return true;

  // 7. 簡短路徑（/:slug）
  const match = normalizedPath.match(/^\/([^\/]+)$/);
  if (match) {
    const slug = match[1];

    // 4a. 靜態頁面（about/faq/terms/privacy）→ 支援 locale URL
    if (STATIC_PAGES.includes(slug)) return true;

    // 4b. 教師 slug（排除系統路徑）→ 支援 locale URL
    if (!SYSTEM_PATHS.includes(slug)) return true;

    // 4c. 系統路徑（dashboard/admin/api）→ 不支援 locale URL
    return false;
  }

  // 其他路徑不支援 locale URL
  return false;
}

/**
 * 切換頁面語言（混合模式）
 * - 白名單路徑：使用 URL locale 切換
 * - 其他路徑：使用 Cookie + URL 參數重新加載
 * @param {string} locale - 要切換到的語言代碼
 */
export function switchLocale(locale) {
  // 確保配置已載入
  ensureConfigLoaded();

  // 設定 cookie 供後端讀取
  document.cookie = `user_locale=${locale}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;

  const currentPath = window.location.pathname;
  const search = window.location.search;
  const hash = window.location.hash;

  // 檢查當前路徑是否支援 locale URL
  if (!isLocaleUrlSupported(currentPath)) {
    // 非白名單路徑：使用 URL 參數重新加載
    // 使用 URL 參數確保後端立即接收到新的 locale
    const url = new URL(window.location.href);
    url.searchParams.set('locale', locale);
    window.location.href = url.toString();
    return;
  }

  // 白名單路徑：使用 URL locale 切換
  let newPath;

  // 檢查當前路徑格式
  const localeMatch = currentPath.match(/^\/([^\/]+)(\/.*)?$/);
  const firstSegment = localeMatch ? localeMatch[1] : null;
  const restOfPath = localeMatch ? (localeMatch[2] || '') : '';

  // 不區分大小寫檢查是否為支援的 locale
  const isFirstSegmentLocale = firstSegment &&
    SUPPORTED_LOCALES.some(loc => loc.toLowerCase() === firstSegment.toLowerCase());

  if (isFirstSegmentLocale) {
    // 情況 1: 已有語系路徑 (如 /zh-TW/teachers/slug 或 /zh-tw/teachers/slug)
    // 替換現有語系，保持小寫格式（與 Rails routes 一致）
    newPath = `/${locale.toLowerCase()}${restOfPath}`;
  } else if (currentPath === '/') {
    // 情況 2: 根路徑
    newPath = `/${locale.toLowerCase()}`;
  } else if (currentPath === '/tools' || currentPath.startsWith('/tools/')) {
    // 情況 3: 工具頁面（/tools 或 /tools/xxx）
    newPath = `/${locale.toLowerCase()}${currentPath}`;
  } else if (currentPath.match(/^\/[^\/]+$/)) {
    // 情況 4: 簡短路徑（/:slug）
    const slug = firstSegment;

    if (STATIC_PAGES.includes(slug)) {
      // 4a. 靜態頁面 (如 /about) → /locale/about
      newPath = `/${locale.toLowerCase()}/${slug}`;
    } else if (!SYSTEM_PATHS.includes(slug)) {
      // 4b. Team slug (如 /peak-academy) → /locale/slug
      newPath = `/${locale.toLowerCase()}/${slug}`;
    } else {
      // 4c. 不應該到達這裡（系統路徑不支援 locale URL）
      // 但為了安全，添加 fallback
      newPath = `/${locale.toLowerCase()}${currentPath}`;
    }
  } else {
    // 情況 5: 其他白名單路徑 (如 /teachers/slug)
    // 在路徑前加上語系
    newPath = `/${locale.toLowerCase()}${currentPath}`;
  }

  // 保留查詢參數和 hash
  window.location.href = newPath + search + hash;
}

/**
 * 初始化路由配置
 * 從 DOM 讀取後端提供的配置
 * （現在由 ensureConfigLoaded 統一處理）
 */
function initializeRoutesConfig() {
  ensureConfigLoaded();
}

/**
 * 初始化語言選擇器
 * 為所有具有 data-language-switcher 屬性的 select 元素綁定事件
 */
export function initializeLanguageSwitchers() {
  document.addEventListener('DOMContentLoaded', function() {
    // 初始化路由配置
    initializeRoutesConfig();

    // 綁定語言切換事件
    const switchers = document.querySelectorAll('[data-language-switcher]');

    switchers.forEach(switcher => {
      switcher.addEventListener('change', function() {
        switchLocale(this.value);
      });
    });
  });
}

// 自動初始化（如果是在瀏覽器環境中）
if (typeof window !== 'undefined') {
  initializeLanguageSwitchers();
}
